import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Monitor, Download, Database, Shield, RefreshCw, Wifi, WifiOff,
  HardDrive, Lock, Zap, Server, FolderSync, CheckCircle2, ArrowRight,
  Terminal, GitBranch, Package, Cpu, Globe,
} from "lucide-react";

const ARCHITECTURE_LAYERS = [
  {
    name: "Presentation Layer",
    icon: Monitor,
    color: "text-primary",
    bg: "bg-primary/10",
    items: ["React + Vite UI (shared with web)", "Electron BrowserWindow", "System Tray integration", "Native OS notifications"],
  },
  {
    name: "Local Data Layer",
    icon: Database,
    color: "text-emerald-600",
    bg: "bg-emerald-500/10",
    items: ["SQLite (better-sqlite3) local cache", "IndexedDB fallback for web", "AES-256-GCM encrypted storage", "WAL mode for performance"],
  },
  {
    name: "Sync Engine",
    icon: RefreshCw,
    color: "text-blue-600",
    bg: "bg-blue-500/10",
    items: ["Background sync every 5 min", "Batch processing (20 entries/batch)", "Conflict detection & resolution", "Auto-retry with exponential backoff"],
  },
  {
    name: "Security Layer",
    icon: Shield,
    color: "text-amber-600",
    bg: "bg-amber-500/10",
    items: ["PBKDF2 key derivation (100K iterations)", "AES-256-GCM data encryption at rest", "Sandboxed context isolation", "Signed app packages"],
  },
];

const SETUP_STEPS = [
  { step: 1, title: "Export to GitHub", desc: "Click 'Export to GitHub' from the Lovable project menu", icon: GitBranch },
  { step: 2, title: "Clone & Install", desc: "git clone <repo> && cd accoupro && npm install", icon: Terminal },
  { step: 3, title: "Install Electron", desc: "npm install electron electron-builder better-sqlite3 --save-dev", icon: Package },
  { step: 4, title: "Build & Package", desc: "npm run build && npx electron-builder --win --mac --linux", icon: Cpu },
  { step: 5, title: "Distribute", desc: "Upload installers to your download page or auto-update server", icon: Globe },
];

const OFFLINE_CAPABILITIES = [
  { feature: "Create Invoices", status: "supported", desc: "Full GST invoice creation with local storage" },
  { feature: "Voucher Entry", status: "supported", desc: "Journal, Sales, Purchase, Receipt, Payment, Contra" },
  { feature: "Record Expenses", status: "supported", desc: "Expense recording with category and attachments" },
  { feature: "Customer Entries", status: "supported", desc: "Add/edit customer and party records" },
  { feature: "Inventory Updates", status: "supported", desc: "Stock item creation and quantity adjustments" },
  { feature: "View Ledgers", status: "supported", desc: "Cached ledger data available offline" },
  { feature: "GST Filing", status: "online-only", desc: "Requires government portal connectivity" },
  { feature: "Bank Sync", status: "online-only", desc: "Requires bank API connection" },
  { feature: "AI Features", status: "online-only", desc: "Requires cloud AI processing" },
];

const DesktopAppPage = () => {
  const [encryptionEnabled, setEncryptionEnabled] = useState(true);
  const [autoSync, setAutoSync] = useState(true);
  const [syncInterval, setSyncInterval] = useState(5);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <Monitor className="h-6 w-6 text-primary" />
            Desktop Application
          </h1>
          <p className="text-sm text-muted-foreground">Electron-based desktop app with offline support and automatic sync</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-1.5" /> Download for Windows
          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-1.5" /> Download for macOS
          </Button>
        </div>
      </div>

      <Tabs defaultValue="architecture">
        <TabsList className="grid grid-cols-4 w-full max-w-lg">
          <TabsTrigger value="architecture">Architecture</TabsTrigger>
          <TabsTrigger value="offline">Offline Mode</TabsTrigger>
          <TabsTrigger value="setup">Setup Guide</TabsTrigger>
          <TabsTrigger value="config">Configuration</TabsTrigger>
        </TabsList>

        {/* Architecture Tab */}
        <TabsContent value="architecture" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {ARCHITECTURE_LAYERS.map((layer) => (
              <Card key={layer.name} className="border-border/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <div className={`p-2 rounded-lg ${layer.bg}`}>
                      <layer.icon className={`h-4 w-4 ${layer.color}`} />
                    </div>
                    {layer.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {layer.items.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Data Flow Diagram */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-base">Data Flow Architecture</CardTitle>
              <CardDescription>How data moves between local cache and cloud</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center gap-3 flex-wrap py-4">
                {[
                  { icon: Monitor, label: "Desktop App", sub: "Electron + React" },
                  { icon: ArrowRight, label: "", sub: "" },
                  { icon: HardDrive, label: "Local SQLite", sub: "Encrypted Cache" },
                  { icon: ArrowRight, label: "", sub: "" },
                  { icon: FolderSync, label: "Sync Engine", sub: "Background Worker" },
                  { icon: ArrowRight, label: "", sub: "" },
                  { icon: Lock, label: "Encryption", sub: "AES-256-GCM" },
                  { icon: ArrowRight, label: "", sub: "" },
                  { icon: Server, label: "Cloud API", sub: "Lovable Cloud" },
                ].map((node, i) => (
                  node.label === "" ? (
                    <ArrowRight key={i} className="h-5 w-5 text-muted-foreground/50" />
                  ) : (
                    <div key={i} className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-muted/30 border border-border/50 min-w-[90px]">
                      <node.icon className="h-5 w-5 text-primary" />
                      <span className="text-xs font-semibold text-foreground">{node.label}</span>
                      <span className="text-[10px] text-muted-foreground">{node.sub}</span>
                    </div>
                  )
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Offline Mode Tab */}
        <TabsContent value="offline" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <WifiOff className="h-4 w-4 text-destructive" /> Offline Capabilities
                </CardTitle>
                <CardDescription>Features available without internet</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {OFFLINE_CAPABILITIES.map((cap) => (
                    <div key={cap.feature} className="flex items-center justify-between py-2 border-b border-border/30 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-foreground">{cap.feature}</p>
                        <p className="text-xs text-muted-foreground">{cap.desc}</p>
                      </div>
                      <Badge variant={cap.status === "supported" ? "default" : "secondary"} className="text-[10px]">
                        {cap.status === "supported" ? "✓ Offline" : "☁ Online Only"}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <Card className="border-border/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Wifi className="h-4 w-4 text-emerald-500" /> Auto-Sync Behavior
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                  <div className="flex items-start gap-2">
                    <Zap className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <span>When internet reconnects, sync starts automatically within 2 seconds</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <RefreshCw className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <span>Background sync runs every {syncInterval} minutes while online</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Shield className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <span>All data encrypted with AES-256-GCM before local storage</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Database className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <span>SQLite with WAL mode ensures fast reads during sync writes</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Conflict Resolution</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-muted-foreground">
                  <p>When the same record is edited both locally and on cloud:</p>
                  <ul className="space-y-1 ml-4">
                    <li className="list-disc">Conflict is flagged in the Sync Dashboard</li>
                    <li className="list-disc">Side-by-side comparison of both versions shown</li>
                    <li className="list-disc">User chooses: Keep Local, Keep Cloud, or Merge</li>
                    <li className="list-disc">Resolution is encrypted and synced to cloud</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Setup Guide Tab */}
        <TabsContent value="setup" className="space-y-4">
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-base">Build Desktop App — Step by Step</CardTitle>
              <CardDescription>Convert AccouPro web app into a native desktop application</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {SETUP_STEPS.map((step) => (
                  <div key={step.step} className="flex items-start gap-4 p-4 rounded-xl bg-muted/30 border border-border/50">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold text-sm shrink-0">
                      {step.step}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <step.icon className="h-4 w-4 text-primary" />
                        <span className="font-semibold text-sm text-foreground">{step.title}</span>
                      </div>
                      <code className="text-xs bg-muted px-2 py-1 rounded text-muted-foreground font-mono">{step.desc}</code>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-base">Electron Configuration</CardTitle>
              <CardDescription>Key files in the desktop architecture</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  { file: "electron/main.ts", desc: "Main process — window management, tray, IPC handlers" },
                  { file: "electron/preload.ts", desc: "Preload — secure bridge between main and renderer" },
                  { file: "electron/database.ts", desc: "SQLite schema and CRUD operations" },
                  { file: "src/lib/offline/encryption.ts", desc: "AES-256-GCM encryption for data at rest" },
                  { file: "src/lib/offline/backgroundSync.ts", desc: "Background sync engine with batch processing" },
                  { file: "src/lib/offline/db.ts", desc: "IndexedDB layer (web fallback)" },
                ].map((f) => (
                  <div key={f.file} className="p-3 rounded-lg bg-muted/30 border border-border/50">
                    <code className="text-xs font-mono font-semibold text-primary">{f.file}</code>
                    <p className="text-xs text-muted-foreground mt-1">{f.desc}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configuration Tab */}
        <TabsContent value="config" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="text-base">Sync Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Auto Sync</Label>
                    <p className="text-xs text-muted-foreground">Automatically sync when online</p>
                  </div>
                  <Switch checked={autoSync} onCheckedChange={setAutoSync} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Sync Interval</Label>
                    <p className="text-xs text-muted-foreground">Minutes between sync attempts</p>
                  </div>
                  <select
                    value={syncInterval}
                    onChange={(e) => setSyncInterval(Number(e.target.value))}
                    className="text-sm border border-border rounded-lg px-3 py-1.5 bg-background"
                  >
                    <option value={1}>1 min</option>
                    <option value={5}>5 min</option>
                    <option value={15}>15 min</option>
                    <option value={30}>30 min</option>
                  </select>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Batch Size</Label>
                    <p className="text-xs text-muted-foreground">Entries per sync batch</p>
                  </div>
                  <Badge variant="outline">20 entries</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Max Retries</Label>
                    <p className="text-xs text-muted-foreground">Retry attempts before marking failed</p>
                  </div>
                  <Badge variant="outline">5 attempts</Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="text-base">Security Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Data Encryption</Label>
                    <p className="text-xs text-muted-foreground">Encrypt all local data at rest</p>
                  </div>
                  <Switch checked={encryptionEnabled} onCheckedChange={setEncryptionEnabled} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Algorithm</Label>
                    <p className="text-xs text-muted-foreground">Encryption cipher</p>
                  </div>
                  <Badge variant="outline">AES-256-GCM</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Key Derivation</Label>
                    <p className="text-xs text-muted-foreground">PBKDF2 iterations</p>
                  </div>
                  <Badge variant="outline">100,000</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Context Isolation</Label>
                    <p className="text-xs text-muted-foreground">Sandboxed renderer process</p>
                  </div>
                  <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">Enabled</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DesktopAppPage;
